import React from 'react';
import path from 'path';
import { promises as fs } from 'fs';
import { NextSeo } from 'next-seo';
import PropTypes from 'prop-types';
import { CurrentItemPage } from '../../components/Views/CurrentItemPage';
import Layout from '../../components/Layout/Layout';
import { makeHostSum, normalizePath } from '../../utils/helpers';
import initApollo from '../../utils/init-Apollo';
import { getLocaleOnSSR } from '../../utils/helpers/getLocaleOnSSR';
import {
  getCurrentItemData,
  getResStatusCode,
  normalizeServerPagePath
} from '../../utils/helpers/item';
import { generateItemSeoObj } from '../../utils/helpers/seo';
import { resolveCurrentItemMockFile } from '../../utils/helpers/resolveCurrentItemMockFile';
import {
  AnalyticLogEventTypeEnum,
  AnalyticLogTargetTypeEnum
} from '../../graphql/types';
import { getLogSource } from '../../utils/helpers/getSSRPlatform';
import { sendAnalyticsDataMutation } from '../../api/mutations';
import { AnalyticLogSourceEnum } from '../../graphql/graphql';
import { getSSRContextData } from '../../utils/helpers/ssrContext';
import { signRequest } from '../../utils/helpers/signRequest';
import { withBugsnagGetServerSideProps } from '../../utils/withBugsnag';

const CurrentItem = props => {
  const mainPageImage = props?.mainPageImage?.full;
  return (
    <>
      {mainPageImage && (
        <NextSeo
          noindex={false}
          nofollow={false}
          robotsProps={{
            maxImagePreview: 'large'
          }}
        />
      )}
      <CurrentItemPage {...props} />
    </>
  );
};

CurrentItem.propTypes = {
  mainPageImage: PropTypes.shape({
    full: PropTypes.string
  })
};

const getCurrentItemMockData = async (params, query) => {
  const filePath = resolveCurrentItemMockFile(params.id, query);
  const currentData = path.join(
    process.cwd(),
    'cypress/mock/current_item',
    filePath
  );

  const file = await fs.readFile(currentData, 'utf-8');
  const raw = JSON.parse(file);
  return params.id === '3878205'
    ? {
        ...raw,
        data: {
          ...raw.data,
          item: {
            ...raw.data.item,
            isAuthor: false,
            isMine: false
          }
        }
      }
    : raw;
};

export const getServerSideProps = withBugsnagGetServerSideProps(
  async ({ res, req, params, query }) => {
    const { platform, source, isSSRWebView, userAgent, cookie } =
      getSSRContextData(req);

    const ipAddr =
      req.headers['cf-connecting-ip'] || req.headers['x-forwarded-for'] || null;
    const countryCode =
      req.headers['cf-ipcountry'] || req.headers['x-vercel-ip-country'] || '';

    const { signature, timestamp, hasSecretKeyPresent } = signRequest(
      ipAddr,
      countryCode
    );

    if (query.mock === 'true' && process.env.NODE_ENV !== 'production') {
      const mock = await getCurrentItemMockData(params, query);
      return {
        props: {
          currentItemData: mock.data.item,
          mainPageImage: mock.data.item?.photos?.[0] || {},
          hasSecretKeyPresent
        }
      };
    }

    const currentLang = getLocaleOnSSR(req);
    const apolloClient = initApollo(
      {},
      {
        lang: currentLang,
        platform,
        countryCode,
        ipAddr,
        userAgent,
        signature,
        timestamp,
        cookie
      }
    );

    const currentAsPath = normalizeServerPagePath(req.url);
    const newAsPath = normalizePath(currentAsPath);
    const hostSum = makeHostSum({ asPath: newAsPath, req });
    const itemId = params.id;

    const show404Page = response => {
      response.statusCode = 404;
      return { props: { statusCode: 404 } };
    };

    if (res && !currentAsPath.match(/\d+/)?.[0]) return show404Page(res);

    const currentItemRes = await getCurrentItemData(apolloClient, itemId);

    const { currentItemData, errorCode } = currentItemRes;
    let isSentHitEvent = false;

    // Log views for Desktop users (both real users and crawlers).
    // On the server, we can securely sign the request with a timestamp.
    // Note: Mobile real users are logged via client-side useEffect to filter bots.
    if (currentItemData?.id && platform === 'desktop' && signature) {
      try {
        await apolloClient.mutate({
          mutation: sendAnalyticsDataMutation,
          variables: {
            targetId: currentItemData.id,
            targetType: AnalyticLogTargetTypeEnum.Item,
            sourceLink: hostSum.currentUrl,
            eventType: AnalyticLogEventTypeEnum.ItemHit,
            source: getLogSource(AnalyticLogSourceEnum, source, isSSRWebView)
          }
        });
        isSentHitEvent = true;
      } catch (error) {
        console.error('SendAnalyticsDataMutation:', error);
      }
    }

    const seoData = generateItemSeoObj(currentItemData, newAsPath, hostSum);

    const statusCode = getResStatusCode(currentItemData);
    if (statusCode || errorCode) res.statusCode = errorCode || statusCode;

    return {
      props: {
        newAsPath,
        isSentHitEvent,
        hostSum,
        itemId,
        seoData,
        mainPageImage: currentItemData?.photos?.[0] || {},
        hasSecretKeyPresent,
        currentLang,
        apolloCache: apolloClient.cache.extract(),
        ...currentItemRes
      }
    };
  },
  '/items/[id]'
);

CurrentItem.getLayout = (page, pageProps) => (
  <Layout
    isSafeAreaTop
    isBottomNavigation={false}
    isCurrentItemPage
    isBanner
    pageProps={pageProps}
    isBackgroundBanner
    hideMobileHeader
  >
    {page}
  </Layout>
);

export default CurrentItem;
